import React from "react"
import { MicrophoneSlash, SpinnerGap } from "@phosphor-icons/react"
import { cn } from "../../../lib/utils"
import { AgentAvatar } from "../AgentAvatar"
import { useAuthStore } from "../../../stores"
import { enableDialing } from "../../../lib/twilio"
import { requestNotificationPermission } from "../../../lib/twilio/incoming-call-alerts"
import { useCallStore } from "../../../stores"

interface NotSetupStateProps {
  collapsed?: boolean
}

export function NotSetupState({ collapsed }: NotSetupStateProps) {
  const { user } = useAuthStore()
  const { browserDialing } = useCallStore()

  const isLoading = browserDialing === "loading"

  const initials = user
    ? `${user.given_name?.[0] || ""}${user.family_name?.[0] || ""}`.toUpperCase()
    : "NA"

  const displayName = user
    ? `${user.given_name || ""} ${user.family_name || ""}`.trim()
    : "Not Assigned"

  const handleEnableAudio = async () => {
    await enableDialing()
    requestNotificationPermission()
  }

  if (collapsed) {
    return (
      <div className="py-3 bg-[#0d1f2d]">
        <div className="flex flex-col items-center">
          <AgentAvatar initials={initials} status="available" size="sm" />
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 py-5 bg-[#0d1f2d]">
      <div className="flex flex-col items-center">
        {/* Avatar */}
        <AgentAvatar initials={initials} status="available" size="md" />

        {/* Name */}
        <p className="mt-3 text-[13px] font-semibold text-white">{displayName}</p>

        {/* Audio status */}
        <div className="mt-1.5 flex items-center gap-1.5">
          <MicrophoneSlash className="h-3 w-3 text-white/50" />
          <span className="text-[10px] text-white/60">
            {isLoading ? "Setting Up Audio..." : "Audio Not Enabled"}
          </span>
        </div>

        {/* Enable Audio button */}
        <button
          onClick={handleEnableAudio}
          disabled={isLoading}
          className={cn(
            "relative mt-4 flex items-center justify-center gap-2 rounded-full px-5 py-2.5",
            "text-[11px] font-bold uppercase tracking-wide",
            "transition-all duration-200 shadow-lg",
            "bg-gradient-to-r from-primary to-primary/80 text-white",
            "hover:shadow-primary/30 hover:shadow-xl hover:scale-[1.02]",
            "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
            "audio-enable-glow"
          )}
        >
          {isLoading ? (
            <>
              <SpinnerGap className="h-3.5 w-3.5 animate-spin" />
              <span>Setting Up...</span>
            </>
          ) : (
            <>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
              <span>Enable Audio</span>
            </>
          )}
        </button>
      </div>
    </div>
  )
}
